import { env } from "@/lib/env"

/** Header the guest create-thread call carries; the server verifies it before giving out the free search. */
export const TURNSTILE_HEADER = "X-Turnstile-Token"
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
const TIMEOUT_MS = 15_000

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  remove: (id: string) => void
}
declare global {
  interface Window { turnstile?: TurnstileApi }
}

let loading: Promise<TurnstileApi | null> | null = null

/** Load Cloudflare's script once, on first use. Null when it cannot load. */
function loadScript(): Promise<TurnstileApi | null> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  loading ??= new Promise((resolve) => {
    const s = document.createElement("script")
    s.src = SCRIPT_SRC
    s.async = true
    s.onload = () => { loading = null; resolve(window.turnstile ?? null) }
    s.onerror = () => { loading = null; s.remove(); resolve(null) }
    document.head.appendChild(s)
  })
  return loading
}

/**
 * A fresh single-use Turnstile token, or null when the check is off (no site key) or could not run
 * (script blocked, widget error, timeout). Callers send the request either way; the server decides.
 * The widget is "interaction-only": invisible unless Cloudflare needs the visitor to do something.
 */
export async function getTurnstileToken(): Promise<string | null> {
  if (!env.VITE_TURNSTILE_SITE_KEY) return null
  const api = await loadScript().catch(() => null)
  if (!api) return null
  return new Promise((resolve) => {
    const host = document.createElement("div")
    document.body.appendChild(host)
    let id: string | undefined
    let done = false
    const finish = (token: string | null) => {
      if (done) return
      done = true
      clearTimeout(timer)
      try { if (id) api.remove(id) } catch { /* widget already gone */ }
      host.remove()
      resolve(token)
    }
    const timer = setTimeout(() => finish(null), TIMEOUT_MS)
    try {
      id = api.render(host, {
        sitekey: env.VITE_TURNSTILE_SITE_KEY,
        appearance: "interaction-only",
        callback: (token: string) => finish(token),
        "error-callback": () => finish(null),
        "timeout-callback": () => finish(null),
      })
    } catch {
      finish(null)
    }
  })
}

/** The server's sentence for a refused bot check (403 bot_check_failed / 503 bot_check_unavailable), else null. */
export function botCheckMessage(err: unknown): string | null {
  const e = err as { status?: unknown; message?: unknown } | null
  if (!e || (e.status !== 403 && e.status !== 503)) return null
  return typeof e.message === "string" && e.message && !e.message.startsWith("HTTP ") ? e.message : null
}
